import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Users, Ticket, Map as MapIcon, LayoutGrid, Palette, RefreshCw, LogOut, Globe, ChevronRight, ChevronDown, CalendarDays, X } from 'lucide-react';
import StampRallyAdmin from './StampRallyAdmin';

// Mobile-only navigation for the dashboard (hidden from md breakpoint up).
// Reuses the existing tab keys, so every desktop feature stays reachable.

const GROUPS = {
  pendaftar: ['registrations', 'corporate', 'fnb'],
  event: ['floorplan', 'lineup', 'media'],
};
const MAIN_KEYS = ['analytics', ...GROUPS.pendaftar, ...GROUPS.event];
const SUB_LABEL = { registrations: 'Creator', corporate: 'Brand Booth', fnb: 'F&B', floorplan: 'Floorplan', lineup: 'Line Up', media: 'Media' };

const pendingOf = (arr) => (arr || []).filter((r) => (r.status || 'pending') === 'pending').length;
const groupOf = (tab) => (GROUPS.pendaftar.includes(tab) ? 'pendaftar' : GROUPS.event.includes(tab) ? 'event' : tab === 'analytics' ? 'home' : 'more');

// True for light backgrounds (used to switch the dashboard into the clean light colorway).
export const isLightColor = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) > 160;
};

const EVENT_DAY = '2026-12-12';
const daysToEvent = () => {
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${EVENT_DAY}T00:00:00`) - today) / 86400000);
};

export function MobileHero({ online = 0, regs, corporate, fnb }) {
  const pending = pendingOf(regs) + pendingOf(corporate) + pendingOf(fnb);
  const d = daysToEvent();
  return (
    <div data-testid="mobile-hero" className="md:hidden relative overflow-hidden rounded-3xl p-5 mb-4 text-white"
      style={{ background: 'linear-gradient(135deg, var(--tv-accent), color-mix(in srgb, var(--tv-accent) 65%, #000))' }}>
      <div aria-hidden="true" className="absolute -right-12 -top-14 w-48 h-48 rounded-full" style={{ background: "rgba(255,255,255,0.1)" }} />
      <div aria-hidden="true" className="absolute -left-10 -bottom-12 w-32 h-32 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }} />
      <div className="relative flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-black leading-tight">Halo, Tim Talesverse</div>
          <div className="text-sm font-medium mt-1">
            {pending > 0 ? `${pending} pendaftar menunggu review.` : 'Semua pendaftar sudah direview.'}
          </div>
        </div>
        <img src="/mascot/wink.webp" alt="" className="w-[88px] h-auto flex-none -mr-1 -my-2 drop-shadow-lg" />
      </div>
      <div className="relative mt-4 rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold">Menuju Memories Market</div>
          <div className="text-3xl font-black leading-none mt-1">{d > 0 ? `H-${d}` : d === 0 ? 'Hari H' : 'Selesai'}</div>
          <div className="text-xs font-medium mt-1">Sabtu, 12 Des 2026</div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#fff' }}>
          <span className="w-2 h-2 rounded-full bg-emerald-500" />{online} online
        </span>
      </div>
    </div>
  );
}

// Collapsible on phones (a compact "Periode" button), always open on desktop.
export function MobileCollapse({ title = 'Periode & pembanding', hint = 'Ketuk untuk ubah rentang tanggal', children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="tv-mcollapse">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="mobile-period-toggle"
        className="md:hidden w-full flex items-center gap-3 rounded-2xl px-4 min-h-[56px] bg-white/5 border border-white/10 text-left text-white">
        <span className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: 'color-mix(in srgb, var(--tv-accent) 20%, transparent)', color: 'var(--tv-accent)' }}>
          <CalendarDays className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-bold text-[15px]">{title}</span>
          <span className="block text-xs text-white/60">{hint}</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-white/60 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={open ? 'mt-3 md:mt-0' : 'hidden md:block'}>{children}</div>
    </div>
  );
}

const PAGE = {
  pendaftar: { title: 'Pendaftar', sub: 'Review, undang & pantau pembayaran' },
  event: { title: 'Event & Booth', sub: 'Memories Market · 12 Des 2026', mascot: '/mascot/run.webp' },
};

// Mobile page header for Pendaftar / Event: colored card + segmented tabs (like the prototype).
export function MobileSubTabs({ tab, setTab, regs, corporate, fnb, lineup, mediaPartners }) {
  const g = groupOf(tab);
  if (g !== 'pendaftar' && g !== 'event') return null;
  const page = PAGE[g];
  const counts = { registrations: regs?.length, corporate: corporate?.length, fnb: fnb?.length, lineup: lineup?.length, media: mediaPartners?.length };
  return (
    <div data-testid={`mobile-page-${g}`} className="md:hidden relative overflow-hidden rounded-3xl p-4 mb-4 text-white"
      style={{ background: 'linear-gradient(135deg, var(--tv-accent), color-mix(in srgb, var(--tv-accent) 65%, #000))' }}>
      <div aria-hidden="true" className="absolute -right-12 -top-14 w-44 h-44 rounded-full" style={{ background: "rgba(255,255,255,0.1)" }} />
      <div className="relative flex items-center gap-2 px-1">
        <div className="flex-1 min-w-0 py-1">
          <div className="text-2xl font-black leading-tight">{page.title}</div>
          <div className="text-sm font-medium mt-0.5">{page.sub}</div>
        </div>
        {page.mascot && <img src={page.mascot} alt="" className="h-[76px] w-auto flex-none -my-3 drop-shadow-lg" />}
      </div>
      <div className="relative mt-3 flex gap-1 p-1 rounded-2xl" style={{ background: 'rgba(0,0,0,0.22)' }}>
        {GROUPS[g].map((k) => {
          const on = tab === k;
          return (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`mobile-sub-${k}`} aria-pressed={on}
              className="flex-1 min-h-[46px] rounded-xl text-[13px] font-bold leading-tight transition"
              style={on ? { background: '#fff', color: 'var(--tv-accent)' } : { color: 'rgba(255,255,255,0.9)' }}>
              {SUB_LABEL[k]}
              {counts[k] != null && <span className="block text-[11px] font-semibold" style={{ opacity: 0.85 }}>{counts[k]}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MobileNav({ nav, tab, setTab, regs, corporate, fnb, onAppearance, onRefresh, onLogout }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [stampOpen, setStampOpen] = useState(false);

  // Mobile: label every table cell from its column header so CSS can show rows as cards.
  useEffect(() => {
    const main = document.querySelector('.tv-dash main');
    if (!main) return undefined;
    let raf = 0;
    const apply = () => {
      raf = 0;
      main.querySelectorAll('table').forEach((t) => {
        const ths = [...t.querySelectorAll('thead th')].map((th) => th.innerText.trim());
        if (!ths.length) return;
        t.classList.add('tv-cards');
        const pi = ths.findIndex((h) => /brand|company|business|tenant|nama|name/i.test(h));
        t.querySelectorAll('tbody > tr').forEach((tr) => {
          const single = tr.children.length === 1;
          [...tr.children].forEach((td, i) => {
            const h = single ? '' : (ths[i] || '');
            if (td.getAttribute('data-label') !== h) td.setAttribute('data-label', h);
            td.toggleAttribute('data-primary', !single && i === pi);
            td.toggleAttribute('data-actions', /action|aksi/i.test(h));
            td.toggleAttribute('data-status', /^status$/i.test(h));
            td.toggleAttribute('data-select', !single && h === '' && !!td.querySelector('input[type="checkbox"]'));
          });
        });
      });
    };
    apply();
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(apply); });
    mo.observe(main, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const g = groupOf(tab);
  // Lets the stylesheet tune each mobile page (pendaftar / event / home / more).
  useEffect(() => {
    const r = document.querySelector('.tv-dash');
    if (r) r.setAttribute('data-mgroup', g);
  }, [g]);
  const pending = pendingOf(regs) + pendingOf(corporate) + pendingOf(fnb);
  const extra = (nav || []).filter((n) => !MAIN_KEYS.includes(n.key));
  const go = (k) => (e) => { e.stopPropagation(); setTab(k); setMoreOpen(false); };
  const color = (on) => (on ? { color: 'var(--tv-accent)' } : undefined);
  const item = 'flex flex-col items-center justify-center gap-1 h-14 text-[11px] font-bold';

  return (
    <>
      <nav aria-label="Navigasi utama" data-testid="mobile-bottom-nav"
        className="md:hidden fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 items-end px-1.5 pb-[max(10px,env(safe-area-inset-bottom))] pt-1 bg-[#160F0F]/95 backdrop-blur-xl border-t border-white/10 rounded-t-3xl">
        <button type="button" onClick={go('analytics')} className={`${item} ${g === 'home' ? '' : 'text-white/60'}`} style={color(g === 'home')}>
          <Home className="w-5 h-5" />Beranda
        </button>
        <button type="button" onClick={go('registrations')} className={`${item} ${g === 'pendaftar' ? '' : 'text-white/60'}`} style={color(g === 'pendaftar')}
          aria-label={pending > 0 ? `Pendaftar, ${pending} menunggu review` : 'Pendaftar'}>
          <span className="relative">
            <Users className="w-5 h-5" />
            {pending > 0 && (
              <span className="absolute -top-2 left-3 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black grid place-items-center text-white" style={{ background: '#DC2626' }}>
                {pending > 99 ? '99+' : pending}
              </span>
            )}
          </span>
          Pendaftar
        </button>
        <div className="flex flex-col items-center justify-end h-14">
          <button type="button" onClick={(e) => { e.stopPropagation(); setMoreOpen(false); setStampOpen(true); }} aria-label="Buka Stamp Rally" data-testid="mobile-stamp-btn"
            className="-mt-8 w-16 h-16 rounded-full grid place-items-center text-white border-4 border-[#0F0A0A] shadow-xl"
            style={{ background: 'var(--tv-accent)' }}>
            <Ticket className="w-7 h-7" />
          </button>
          <span className={`text-[11px] font-bold mt-0.5 ${stampOpen ? '' : 'text-white/60'}`} style={color(stampOpen)}>Stamp Rally</span>
        </div>
        <button type="button" onClick={go('floorplan')} className={`${item} ${g === 'event' ? '' : 'text-white/60'}`} style={color(g === 'event')}>
          <MapIcon className="w-5 h-5" />Event
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); setMoreOpen(true); }} className={`${item} ${g === 'more' ? '' : 'text-white/60'}`} style={color(g === 'more')}>
          <LayoutGrid className="w-5 h-5" />Lainnya
        </button>
      </nav>

      {stampOpen && <StampRallyAdmin onClose={() => setStampOpen(false)} />}

      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" onClick={(e) => e.stopPropagation()}>
          <button type="button" aria-label="Tutup" onClick={() => setMoreOpen(false)} className="absolute inset-0 w-full h-full bg-black/60" />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-[#160F0F] border-t border-white/10 px-5 pt-3 pb-8 text-white">
            <div className="w-10 h-1.5 rounded-full bg-white/20 mx-auto mb-2" />
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1">
                <div className="text-lg font-black">Lainnya</div>
                <div className="text-sm text-white/70">Website, tracking &amp; pengaturan</div>
              </div>
              <img src="/mascot/welcome.webp" alt="" className="h-20 w-auto -my-2" />
              <button type="button" aria-label="Tutup" onClick={() => setMoreOpen(false)} className="w-11 h-11 rounded-xl grid place-items-center bg-white/10"><X className="w-5 h-5" /></button>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5 overflow-hidden">
              {extra.map((n) => {
                const Icon = n.icon;
                return (
                  <button key={n.key} type="button" onClick={go(n.key)} data-testid={`mobile-more-${n.key}`}
                    className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left">
                    <span className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: 'color-mix(in srgb, var(--tv-accent) 18%, transparent)', color: 'var(--tv-accent)' }}>
                      {Icon && <Icon className="w-5 h-5" />}
                    </span>
                    <span className="flex-1 font-bold text-[15px]">{n.label}</span>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </button>
                );
              })}
            </div>
            <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5 overflow-hidden">
              <button type="button" onClick={(e) => { e.stopPropagation(); setMoreOpen(false); onAppearance && onAppearance(); }} className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left">
                <Palette className="w-5 h-5 text-white/80" /><span className="flex-1 font-bold text-[15px]">Tampilan</span>
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setMoreOpen(false); onRefresh && onRefresh(); }} className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left">
                <RefreshCw className="w-5 h-5 text-white/80" /><span className="flex-1 font-bold text-[15px]">Refresh data</span>
              </button>
              <Link to="/" className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left text-white">
                <Globe className="w-5 h-5 text-white/80" /><span className="flex-1 font-bold text-[15px]">Buka public site</span>
              </Link>
              <button type="button" onClick={(e) => { e.stopPropagation(); onLogout && onLogout(); }} className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left text-red-400">
                <LogOut className="w-5 h-5" /><span className="flex-1 font-bold text-[15px]">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
